import React, { useCallback, useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { getScene, updateScene, listCardsForScene, updateCard, getSceneViews, getSceneTopCards } from "../../utils/api";
import { SceneWithStats, Card, SceneStats, SceneTopCardsStats } from "../../common/types";
import { FaEdit, FaSave, FaTimes, FaChevronDown, FaChevronUp, FaPlus, FaSync, FaQrcode, FaDownload, FaSpinner } from "react-icons/fa";
import { generateCardDesign } from "../../utils/generateCardDesign";
import QREditor from "./QREditor";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import DurationSelector from "../../common/DurationSelector";
import { useDurationFilter } from "../../context/DurationFilterContext";
import "./Scene.css";
import { useScenes } from "../../context/ScenesContext";

const Edit = FaEdit as unknown as React.FC;
const Save = FaSave as unknown as React.FC;
const Cancel = FaTimes as unknown as React.FC;
const ChevronDown = FaChevronDown as unknown as React.FC;
const ChevronUp = FaChevronUp as unknown as React.FC;
const Plus = FaPlus as unknown as React.FC;
const Sync = FaSync as unknown as React.FC;
const QrCode = FaQrcode as unknown as React.FC;
const Download = FaDownload as unknown as React.FC<{ className?: string }>;
const Spinner = FaSpinner as unknown as React.FC<{ className?: string }>;

const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#7a5d21ff",
  "#9b6868ff",
  "#8dd1e1",
  "#a4de6c",
  "#ed5c57ff",
  "#ffc0cb",
  "#b19cd9",
  "#ffb347",
];

const getColorForCard = (cardId: string) => {
  let hash = 0;
  for (let i = 0; i < cardId.length; i++) {
    hash = cardId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % COLORS.length;
  return COLORS[index];
};

export default function Scene() {
  const { collectionId, sceneId } = useParams<{ collectionId: string; sceneId: string }>();
  const { getFromDate, getToDate } = useDurationFilter();
  const [sceneData, setSceneData] = React.useState<SceneWithStats | null>(null);
  const {
    collectionData,
    setCollectionId,
  } = useScenes();
  const [cards, setCards] = React.useState<Card[]>([]);
  const [stats, setStats] = React.useState<SceneStats | null>(null);
  const [topCardsStats, setTopCardsStats] = React.useState<SceneTopCardsStats | null>(null);
  const [showCards, setShowCards] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showQREditor, setShowQREditor] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [generatingCardId, setGeneratingCardId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editForm, setEditForm] = useState({
    scene_name: "",
    description: "",
    historical_year: "",
    longitude: "",
    latitude: "",
    card_image: ""
  });
  
  useEffect(() => {
    setCollectionId(collectionId);
    return () => setCollectionId(undefined);
  }, [collectionId, setCollectionId]);

  const refreshScene = useCallback(async () => {
    if (!sceneId) return;
    try {
      const result = await getScene(sceneId, getFromDate(), getToDate());
      setSceneData(result);
      setEditForm({
        scene_name: result.scene.scene_name,
        description: result.scene.description || "",
        historical_year: result.scene.historical_year?.toString() || "",
        longitude: result.scene.longitude?.toString() || "",
        latitude: result.scene.latitude?.toString() || "",
        card_image: result.scene.card_image || ""
      });
    } catch (error) {
      console.error("Error loading scene:", error);
    }
  }, [sceneId, getFromDate, getToDate]);

  const loadCards = useCallback(async () => {
    if (!sceneId) return;
    try {
      const result = await listCardsForScene(sceneId);
      setCards(result);
    } catch (error) {
      console.error("Error loading cards:", error);
    }
  }, [sceneId]);

  const refreshCharts = useCallback(async () => {
    if (!sceneId) return;
    try {
      const from = getFromDate();
      const to = getToDate();
      const [viewsData, topCardsData] = await Promise.all([
        getSceneViews(sceneId, from, to),
        getSceneTopCards(sceneId, from, to)
      ]);
      setStats(viewsData);
      setTopCardsStats(topCardsData);
    } catch (error) {
      console.error("Error refreshing charts:", error);
    }
  }, [sceneId, getFromDate, getToDate]);

  React.useEffect(() => {
    refreshScene();
  }, [refreshScene]);

  React.useEffect(() => {
    refreshCharts();
  }, [refreshCharts]);

  React.useEffect(() => {
    if (showCards) {
      loadCards();
    }
  }, [showCards, loadCards]);

  const handleSave = async () => {
    if (!sceneId) return;
    try {
      const updateData: any = {
        scene_name: editForm.scene_name,
        description: editForm.description || null,
        historical_year: editForm.historical_year ? parseInt(editForm.historical_year) : null,
        longitude: editForm.longitude ? parseFloat(editForm.longitude) : null,
        latitude: editForm.latitude ? parseFloat(editForm.latitude) : null,
      };
      
      if (editForm.card_image !== sceneData?.scene.card_image) {
        updateData.card_image = editForm.card_image || null;
      }

      await updateScene(sceneId, updateData);
      setIsEditing(false);
      refreshScene();
    } catch (error) {
      console.error("Error updating scene:", error);
    }
  };

  const handleCancel = () => {
    if (sceneData) {
      setEditForm({
        scene_name: sceneData.scene.scene_name,
        description: sceneData.scene.description || "",
        historical_year: sceneData.scene.historical_year?.toString() || "",
        longitude: sceneData.scene.longitude?.toString() || "",
        latitude: sceneData.scene.latitude?.toString() || "",
        card_image: sceneData.scene.card_image || ""
      });
    }
    setIsEditing(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm({ ...editForm, card_image: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleToggleCard = async (cardId: string, currentEnabled: boolean) => {
    try {
      await updateCard(cardId, { enabled: !currentEnabled });
      loadCards();
      refreshCharts();
      refreshScene();
    } catch (error) {
      console.error("Error toggling card:", error);
    }
  };

  const handleUpdateCardDescription = async (cardId: string, description: string) => {
    try {
      await updateCard(cardId, { description });
      setEditingCardId(null);
      loadCards();
    } catch (error) {
      console.error("Error updating card description:", error);
    }
  };

  const handleGenerateCard = async (cardId: string) => {
    const scene = sceneData?.scene;
    if (!scene?.card_image || !scene?.qr_settings) return;
    setGeneratingCardId(cardId);
    try {
      await generateCardDesign(cardId, scene.card_image, scene.qr_settings, scene.scene_name);
    } catch (err) {
      console.error("Error generating card design:", err);
      alert("Failed to generate card design. Check the console for details.");
    } finally {
      setGeneratingCardId(null);
    }
  };

  const buildMultiLineData = (cards?: SceneTopCardsStats["cards"]) => {
    if (!cards) return [];

    const map = new Map<string, any>();

    for (const card of cards) {
      for (const point of card.views) {
        const key = new Date(point.timestamp).toISOString();

        if (!map.has(key)) {
          map.set(key, { timestamp: key });
        }

        map.get(key)[card.card_id] = point.views;
      }
    }

    // fill missing values
    const allCardIds = cards.map(c => c.card_id);

    const datapoints = Array.from(map.values());
    for (const row of datapoints) {
      for (const id of allCardIds) {
        if (row[id] == null) {
          row[id] = 0;
        }
      }
    }

    return Array.from(map.values()).sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  };

  // Filter cards based on search term
  const filteredCards = React.useMemo(() => {
    if (!searchTerm) return cards;
    const lower = searchTerm.toLowerCase();
    return cards.filter(card => 
      card.id.toLowerCase().includes(lower) ||
      (card.description && card.description.toLowerCase().includes(lower))
    );
  }, [cards, searchTerm]);

  const columnHelper = createColumnHelper<Card>();

  const columns = [
    columnHelper.accessor('id', {
      header: 'Card ID',
      cell: info => <span className="card-id">{info.getValue()}</span>,
    }),
    columnHelper.accessor('enabled', {
      header: 'Status',
      cell: info => (
        <button
          className={`status-badge ${info.getValue() ? 'enabled' : 'disabled'}`}
          onClick={() => handleToggleCard(info.row.original.id, info.getValue())}
        >
          {info.getValue() ? 'Enabled' : 'Disabled'}
        </button>
      ),
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: info => {
        const cardId = info.row.original.id;
        const isEditing = editingCardId === cardId;
        const currentDesc = info.getValue() || '';

        return isEditing ? (
          <div className="inline-edit">
            <input
              type="text"
              defaultValue={currentDesc}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateCardDescription(cardId, e.currentTarget.value);
                } else if (e.key === 'Escape') {
                  setEditingCardId(null);
                }
              }}
              onBlur={(e) => handleUpdateCardDescription(cardId, e.target.value)}
            />
          </div>
        ) : (
          <div
            className="editable-cell"
            onClick={() => setEditingCardId(cardId)}
          >
            {currentDesc || <span className="empty-desc">Click to add description</span>}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'design',
      header: 'Design',
      cell: info => {
        const cardId = info.row.original.id;
        const canGenerate = !!(sceneData?.scene.card_image && sceneData?.scene.qr_settings);
        const isGenerating = generatingCardId === cardId;
        return (
          <button
            className="btn-ark icon-btn"
            title={canGenerate ? 'Download card design as PNG' : 'Add a card image and save QR settings first'}
            disabled={!canGenerate || isGenerating}
            onClick={() => handleGenerateCard(cardId)}
          >
            {isGenerating
              ? <Spinner className="spin" />
              : <Download />}
          </button>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: filteredCards,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  if (!sceneData) {
    return (
      <div className="page-shell">
        <p>Loading scene...</p>
      </div>
    );
  }

  const { scene, topCards, totalViews } = sceneData;

  return (
    <div className="page-shell scene-container">
      <nav className="breadcrumbs">
        <Link to="/">Home</Link>
        <span className="breadcrumb-sep">/</span>
        {collectionData && (
          <>
            <Link to={`/collection/${collectionData.collection.id}`}>{collectionData.collection.name}</Link>
            <span className="breadcrumb-sep">/</span>
          </>
        )}
        <span>{scene.scene_name}</span>
      </nav>

      <div className="scene-header">
        <h1 className="page-title">{scene.scene_name}</h1>
        <div className="scene-actions">
          <Link to={`/new?scene_id=${sceneId}`} className="btn-ark icon-btn">
            <Plus /> Create Cards
          </Link>
          {!isEditing && (
            <button className="btn-ark icon-btn" onClick={() => setIsEditing(true)}>
              <Edit /> Edit
            </button>
          )}
          <button className="btn-ark icon-btn" onClick={() => setShowQREditor(true)}>
            <QrCode /> Edit QR Style
          </button>
        </div>
      </div>

      {isEditing ? (
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="scene_name">Scene Name</label>
            <input
              type="text"
              id="scene_name"
              value={editForm.scene_name}
              onChange={(e) => setEditForm({ ...editForm, scene_name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              rows={4}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="historical_year">Historical Year</label>
              <input
                type="number"
                id="historical_year"
                value={editForm.historical_year}
                onChange={(e) => setEditForm({ ...editForm, historical_year: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="longitude">Longitude</label>
              <input
                type="number"
                step="any"
                id="longitude"
                value={editForm.longitude}
                onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="latitude">Latitude</label>
              <input
                type="number"
                step="any"
                id="latitude"
                value={editForm.latitude}
                onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="card_image">Card Image</label>
            <input
              type="file"
              id="card_image"
              accept="image/*"
              onChange={handleImageUpload}
            />
            {editForm.card_image && (
              <div className="image-preview">
                <img src={editForm.card_image} alt="Card preview" />
              </div>
            )}
          </div>
          <div className="form-actions">
            <button className="btn-ark" onClick={handleSave}>
              <Save /> Save
            </button>
            <button className="btn-secondary" onClick={handleCancel}>
              <Cancel /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="scene-info">
          {scene.description && (
            <div className="info-row">
              <span className="info-label">Description:</span>
              <span className="info-value">{scene.description}</span>
            </div>
          )}
          {scene.historical_year && (
            <div className="info-row">
              <span className="info-label">Historical Year:</span>
              <span className="info-value">{scene.historical_year}</span>
            </div>
          )}
          {(scene.longitude !== undefined && scene.latitude !== undefined) && (
            <div className="info-row">
              <span className="info-label">Location:</span>
              <span className="info-value">{scene.latitude}, {scene.longitude}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Created:</span>
            <span className="info-value">{new Date(scene.created).toLocaleDateString()}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Last Updated:</span>
            <span className="info-value">{new Date(scene.updated).toLocaleDateString()}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Total Views:</span>
            <span className="info-value">{totalViews}</span>
          </div>
          {scene.card_image && (
            <div className="info-row full-width">
              <span className="info-label">Card Image:</span>
              <div className="image-display">
                <img src={scene.card_image} alt="Card" />
              </div>
            </div>
          )}
        </div>
      )}

      <section className="charts-section">
        <div className="stats-header">
          <h2 className="section-title">Analytics</h2>
          <div className="stats-controls">
            <DurationSelector />
            <button className="btn-secondary icon-btn" onClick={refreshCharts}>
              <Sync /> Refresh
            </button>
          </div>
        </div>

        <div className="stats-graph">
          {stats && stats.views.length > 0 ? (
            <div className="chart-card">
              <h3 className="chart-title">Scene Views Over Time</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.views}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={(ts) => new Date(ts).toLocaleDateString()}
                    stroke="#57534e"
                  />
                  <YAxis stroke="#57534e" />
                  <Tooltip 
                    labelFormatter={(ts) => new Date(ts).toLocaleString()}
                    contentStyle={{ 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e7e5e4',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#d97706" 
                    strokeWidth={2}
                    dot={{ fill: '#d97706' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="empty-state">No views in the given period.</p>
          )}

          {topCardsStats && topCardsStats.cards.length > 0 && (
            <div className="chart-card">
              <h3 className="chart-title">Top Cards Performance</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={buildMultiLineData(topCardsStats.cards)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={(ts) => new Date(ts).toLocaleDateString()}
                    stroke="#57534e"
                  />
                  <YAxis stroke="#57534e" />
                  <Tooltip 
                    labelFormatter={(ts) => new Date(ts).toLocaleString()}
                    contentStyle={{ 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e7e5e4',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  {topCardsStats.cards.map((card) => (
                    <Line
                      key={card.card_id}
                      type="monotone"
                      dataKey={card.card_id}
                      name={card.card_id.slice(0, 8)}
                      stroke={getColorForCard(card.card_id)}
                      strokeWidth={2}
                      dot={{ fill: getColorForCard(card.card_id) }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-header">
          <h2 className="section-title">Top Viewed Cards</h2>
          <div className="stats-controls">
            <DurationSelector />
            <button className="btn-secondary icon-btn" onClick={refreshScene}>
              <Sync /> Refresh
            </button>
          </div>
        </div>
        {topCards.length > 0 ? (
          <ul className="stats-list">
            {topCards.map((card) => (
              <li key={card.card_id} className="stats-item">
                <div className="stats-link">
                  <span className="stats-name">{card.card_id}</span>
                  <span className="stats-description">{card.description}</span>
                  <span className="stats-value">{card.total_views} views</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty-state">No view data available yet.</p>
        )}
      </section>

      <section className="cards-section">
        <div className="cards-header">
          <h2 className="section-title">All Cards ({cards.length})</h2>
          <button
            className="btn-secondary icon-btn"
            onClick={() => setShowCards(!showCards)}
          >
            {showCards ? <><ChevronUp /> Hide Cards</> : <><ChevronDown /> Show Cards</>}
          </button>
        </div>

        {showCards && (
          <div className="cards-table-container">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by card ID or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <span className="search-results">
                  {filteredCards.length} of {cards.length} cards
                </span>
              )}
            </div>
            <div className="table-wrapper">
              <table className="cards-table">
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th key={header.id}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {table.getState().pagination.pageIndex + 1} of{' '}
                {table.getPageCount()}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      {showQREditor && (
        <QREditor
          sceneId={sceneId!}
          cardImage={scene.card_image}
          initialSettings={scene.qr_settings}
          onClose={() => setShowQREditor(false)}
          onSaved={(savedSettings) => {
            // Optimistically update qr_settings so reopening the modal
            // immediately shows saved values (no GET refresh timing dependency)
            setSceneData((prev) =>
              prev ? { ...prev, scene: { ...prev.scene, qr_settings: savedSettings } } : prev
            );
            refreshScene();
          }}
        />
      )}
    </div>
  );
}
